import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import CutoutButton from './CutoutButton';
import { cases } from '../data/cases';
import CaseCard from './CaseCard';

const Cases = () => {
    const [activeIndex, setActiveIndex] = useState(0);
    // eslint-disable-next-line no-unused-vars
    const [imagesLoaded, setImagesLoaded] = useState(false);
    const sectionRef = useRef(null);
    const contentRef = useRef(null);
    const navigate = useNavigate();
    const { lang = 'en' } = useParams();

    // Select 3 random cases (memoized so they don't change on re-renders)
    const caseStudies = useMemo(() => {
        const shuffled = [...cases].sort(() => Math.random() - 0.5);
        return shuffled.slice(0, 3);
    }, []);

    // Preload all case images on mount for smooth transitions
    useEffect(() => {
        const imagePromises = caseStudies.map((study) => {
            return new Promise((resolve) => {
                const img = new Image();
                img.onload = resolve;
                img.onerror = resolve; // Still resolve on error to not block
                img.src = study.screenshotHome || study.screenshot;
            });
        });

        Promise.all(imagePromises).then(() => {
            setImagesLoaded(true);
        });
    }, [caseStudies]);

    // Handle scroll to update active case based on scroll position within the section
    useEffect(() => {
        const handleScroll = () => {
            if (!contentRef.current || !sectionRef.current) return;

            const sectionTop = sectionRef.current.offsetTop;
            const scrollPosition = window.scrollY;
            const windowHeight = window.innerHeight;

            // Calculate which case should be active - based on full viewport sections
            const scrollWithinSection = scrollPosition - sectionTop;
            const newIndex = Math.min(
                Math.max(0, Math.round(scrollWithinSection / windowHeight)),
                caseStudies.length - 1
            );

            setActiveIndex(newIndex);
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Initial check

        return () => window.removeEventListener('scroll', handleScroll);
    }, [caseStudies.length]);

    const currentCase = caseStudies[activeIndex];

    return (
        <section
            ref={sectionRef}
            id="cases"
            className="bg-black py-20 pb-8 mb-32 relative"
            data-section="cases"
            style={{ minHeight: window.innerWidth >= 1024 ? `${(caseStudies.length * 90)}vh` : 'auto' }}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    {/* Mobile View - Cards Layout */}
                    <div className="block lg:hidden space-y-12">
                        <div className="mb-8">
                            <p className="text-white/60 text-xs font-bold mb-4 uppercase tracking-widest flex items-center gap-2">
                                <span className="w-2 h-2 bg-white/60 rounded-full"></span>
                                SELECTED CASES
                            </p>
                            <h2 className="text-4xl font-bold text-white">Recent Work</h2>
                        </div>

                        {caseStudies.map((study, index) => (
                            <CaseCard
                                key={study.id}
                                caseStudy={study}
                                index={index}
                                onClick={() => navigate(`/${lang}/cases/${study.id}`)}
                                showDescription={true}
                            />
                        ))}

                        <div className="flex justify-center pt-4">
                            <CutoutButton label="View All Cases" onClick={() => navigate(`/${lang}/cases`)} />
                        </div>
                    </div>

                    {/* Left Side - Scrolling Content - Desktop Only */}
                    <div ref={contentRef} className="hidden lg:block lg:col-span-7 space-y-0">
                        {caseStudies.map((study, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: false, amount: 0.5 }}
                                transition={{ duration: 0.6 }}
                                className={`h-screen flex flex-col ${index === 0 || index === 2 ? 'justify-start pt-20' : 'justify-center'} items-start space-y-6`}
                            >
                                {/* Badge */}
                                <div className="mb-0">
                                    <p className="text-white/60 text-xs font-bold mb-0 uppercase tracking-widest flex items-center gap-2">
                                        <span className="w-2 h-2 bg-white/60 rounded-full"></span>
                                        CASE STUDIES
                                    </p>
                                </div>

                                {/* Project Title */}
                                <div>
                                    <h2 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white -mt-4 mb-3 tracking-tight">
                                        {study.title}
                                    </h2>
                                </div>

                                {/* Tags */}
                                <div className="flex gap-3 flex-wrap">
                                    {study.tags.map((tag, tagIndex) => (
                                        <span
                                            key={tagIndex}
                                            className="px-4 py-2 bg-white/10 backdrop-blur-sm text-white text-sm font-bold rounded-full"
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>

                                {/* Quick Info */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8 lg:gap-16 py-6 border-t border-b border-white/10">
                                    <div>
                                        <p className="text-white/50 text-sm font-semibold mb-2 uppercase tracking-wider">Website</p>
                                        <a
                                            href={study.visitLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-white text-base font-medium hover:text-white/80 transition-colors flex items-center gap-1"
                                        >
                                            {study.website}
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                            </svg>
                                        </a>
                                    </div>
                                    <div>
                                        <p className="text-white/50 text-sm font-semibold mb-2 uppercase tracking-wider">Location</p>
                                        <p className="text-white text-base font-medium">{study.location}</p>
                                    </div>
                                    <div>
                                        <p className="text-white/50 text-sm font-semibold mb-2 uppercase tracking-wider">Industry</p>
                                        <p className="text-white text-base font-medium">{study.industry}</p>
                                    </div>
                                </div>

                                {/* Description */}
                                <div>
                                    <p className="text-white/70 text-base sm:text-lg leading-relaxed max-w-xl">
                                        {study.description}
                                    </p>
                                </div>

                                {/* Metrics */}
                                <div className="grid grid-cols-2 sm:flex gap-6 sm:gap-10 lg:gap-16 w-fit">
                                    {study.metrics.map((metric, metricIndex) => (
                                        <div key={metricIndex} className="flex flex-col items-start">
                                            <p className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-2 h-auto sm:h-16 flex items-start">{metric.value}</p>
                                            <p className="text-white/50 text-xs sm:text-sm font-semibold uppercase tracking-wider leading-tight line-clamp-2">{metric.label}</p>
                                        </div>
                                    ))}
                                </div>

                                {/* CTA Button */}
                                <div>
                                    <button
                                        onClick={() => navigate(`/${lang}/cases/${study.id}`)}
                                        className="group flex items-center gap-3 pl-7 pr-1 py-1 bg-white text-black rounded-full font-bold text-sm hover:bg-white/90 transition-all duration-300"
                                    >
                                        View case
                                        <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center transition-transform group-hover:scale-110">
                                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7M17 7H7M17 7V17" />
                                            </svg>
                                        </div>
                                    </button>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* Right Side - Sticky Asymmetric Image Container - Takes up less space */}
                    <div className="hidden lg:block lg:col-span-5 sticky top-24 h-screen flex items-center justify-end">
                        <div className="relative w-full max-w-[500px]" style={{ height: 'calc(100vh - 8rem)' }}>
                            {/* Asymmetric Image Container with cutout in bottom-right */}
                            <div className="relative w-full h-full">
                                {/* Main image container with rounded corners */}
                                <div
                                    className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 overflow-hidden rounded-3xl"
                                    style={{ filter: 'drop-shadow(0 25px 50px rgba(0, 0, 0, 0.5))' }}
                                >
                                    <AnimatePresence mode="popLayout">
                                        <motion.div
                                            key={activeIndex}
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            transition={{ duration: 0.3, ease: 'easeOut' }}
                                            className="absolute inset-0"
                                        >
                                            <img
                                                src={currentCase.screenshotHome || currentCase.screenshot}
                                                alt={`${currentCase.title} screenshot`}
                                                className="w-full h-full object-cover"
                                                loading="eager"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.parentElement.innerHTML = `
                            <div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                              <div class="text-center p-8">
                                <div class="text-6xl mb-4">📱</div>
                                <p class="text-gray-800 font-bold text-xl">${currentCase.title}</p>
                                <p class="text-gray-500 text-sm mt-2">${currentCase.category}</p>
                              </div>
                            </div>
                          `;
                                                }}
                                            />
                                        </motion.div>
                                    </AnimatePresence>
                                </div>

                                {/* Black rounded rectangle container for button at bottom-right */}
                                <div
                                    className="absolute bottom-0 right-0 bg-black p-3 rounded-3xl"
                                    style={{
                                        filter: 'drop-shadow(0 25px 50px rgba(0, 0, 0, 0.5))',
                                        borderRadius: '32px',
                                        marginBottom: '-8px',
                                        marginRight: '-8px',
                                    }}
                                >
                                    {/* "All Cases" Button */}
                                    <CutoutButton label="All Cases" onClick={() => navigate(`/${lang}/cases`)} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div >
        </section >
    );
};

export default Cases;